import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AdvancedMarker, InfoWindow, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import QuickInfo from './QuickInfo';
import getCountryMarkerImage from '../utilities/getCountryMarkerImage';

import markerImage from '../images/parcelshop-marker-2.png';
import hoverMarkerImage from '../images/parcelshop-marker-hover-2.png';
import inactiveMarkerImage from '../images/parcelshop-marker-inactive.png';

import markerImagePaketParcelLock from '../images/parcelshop-marker-paket-parcel-lock-2.png';
import hoverMarkerImagePaketParcelLock from '../images/parcelshop-marker-hover-paket-parcel-lock-2.png';

const CustomMarker = ({
  parcelShopNumber,
  lat,
  lng,
  countryIsoA2Code,
  isHighlighted,
  isSelected,
  onClick,
  onMouseOver,
  onMouseOut,
  onDetailClick,
  description,
  details,
  isPaketFuxx,
  isPaketParcelLock,
  isDisabled,
  onCloseClick
}) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [icon, setIcon] = useState(markerImage);

  const accessibilityLabel = useMemo(() => {
    const detailData = typeof details === 'object' && details !== null ? details : {};
    const nameCandidates = [description, detailData.description, detailData.name].filter(Boolean);
    const streetLine = [detailData.street, detailData.houseNumber].filter(Boolean).join(' ').trim();
    const cityLine = [detailData.zip, detailData.city].filter(Boolean).join(' ').trim();
    const labelParts = [
      ...nameCandidates,
      streetLine,
      cityLine
    ].map(part => (typeof part === 'string' ? part.trim() : '')).filter((part, index, array) => part && array.indexOf(part) === index);
    if (labelParts.length === 0) {
      return 'PaketShop';
    }
    return `PaketShop ${labelParts.join(', ')}`;
  }, [description, details]);

  useEffect(() => {
    let currentMarkerImage = isPaketParcelLock
      ? getCountryMarkerImage(countryIsoA2Code, 'parcelshop-marker-paket-parcel-lock-2', markerImagePaketParcelLock)
      : getCountryMarkerImage(countryIsoA2Code, 'parcelshop-marker-2', markerImage);

    if (isDisabled) {
      currentMarkerImage = getCountryMarkerImage(countryIsoA2Code, 'parcelshop-marker-inactive', inactiveMarkerImage);
    } else if (isHighlighted || isSelected) {
      currentMarkerImage = isPaketParcelLock
        ? getCountryMarkerImage(countryIsoA2Code, 'parcelshop-marker-hover-paket-parcel-lock-2', hoverMarkerImagePaketParcelLock)
        : getCountryMarkerImage(countryIsoA2Code, 'parcelshop-marker-hover-2', hoverMarkerImage);
    }

    setIcon(currentMarkerImage);
  }, [icon, isPaketParcelLock, countryIsoA2Code, isDisabled, isHighlighted, isSelected]);

  useEffect(() => {
    if (marker?.element instanceof HTMLElement) {
      marker.element.setAttribute('aria-label', accessibilityLabel);
      marker.element.setAttribute('title', accessibilityLabel);
    }
  }, [marker, accessibilityLabel]);

  const handleClick = useCallback(() => {
    onClick(parcelShopNumber, isSelected, isDisabled);
    window.wts.push([
      'send',
      'click',
      { linkId: 'Karte.PaketShop Vorschau' }
    ]);
  }, [parcelShopNumber, isSelected, isDisabled, onClick]);

  const handleMouseOver = useCallback(() => {
    onMouseOver(parcelShopNumber);
  }, [parcelShopNumber, onMouseOver]);

  const handleMouseOut = useCallback(() => {
    onMouseOut(parcelShopNumber);
  }, [parcelShopNumber, onMouseOut]);

  return (
    <AdvancedMarker
      ref={markerRef}
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      position={{ lat, lng }}
      title={accessibilityLabel}
    >
      <img src={icon} width={48} height={48} alt="" />
      {isSelected && typeof details === 'object' && (
        <InfoWindow
          anchor={marker}
          onCloseClick={onCloseClick}
        >
          <QuickInfo
            onDetailClick={onDetailClick}
            {...details}
            showCallbackButton={!isDisabled}
          />
        </InfoWindow>
      )}
    </AdvancedMarker>
  );
};

CustomMarker.propTypes = {
  parcelShopNumber: PropTypes.string.isRequired,
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  countryIsoA2Code: PropTypes.string.isRequired,
  isHighlighted: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onMouseOver: PropTypes.func.isRequired,
  onMouseOut: PropTypes.func.isRequired,
  onDetailClick: PropTypes.func,
  description: PropTypes.string,
  details: PropTypes.object,
  isPaketFuxx: PropTypes.bool,
  isPaketParcelLock: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onCloseClick: PropTypes.func.isRequired
};

export default CustomMarker;
